import { clamp } from "../utils/math";
import { selectWeightedDistinct, type Rng } from "../utils/random";
import type { GenreFeatures, RhythmRole, RoleRhythmState, StepPattern } from "./musicTypes";

/**
 * Rhythm generation.
 *
 * Two rules shape the whole file:
 *
 * 1. A step is never decided by its own coin flip. Each bar builds a *metric
 *    weight profile* — how much each step "wants" to sound, given the genre
 *    weights — and then selects a target number of steps from it. Uniform
 *    per-step randomness produces noise; weighted selection produces rhythm.
 *
 * 2. Density decides HOW MANY events exist. Energy decides HOW they are played.
 *    Nothing in this file lets Energy add or remove a note, and nothing lets
 *    Density change a velocity. That separation is what makes
 *    "high energy / low density" a real musical state rather than a slider mix.
 */

/** Absolute ceiling per role per bar. Protects the dispatcher from runaway plans. */
export const MAX_EVENTS_PER_ROLE = 24;

const STEP_COUNT_CHOICES = [16, 12, 15, 17, 14] as const;

export interface RhythmContext {
  features: GenreFeatures;
  /** 0..1 — the ONLY input that changes how many events are produced. */
  density: number;
  rhythmicComplexity: number;
  grooveStrength: number;
  silenceProbability: number;
  /** True when this bar should be a fill (section end, etc.). */
  isFill: boolean;
  rng: Rng;
}

/**
 * Steps-per-bar for the bar. Mostly 16; polymeter values appear when the genre
 * mix (techno with loose groove, or jazz) asks for them.
 */
export function chooseStepsPerBar(features: GenreFeatures, rng: Rng): number {
  if (!rng.chance(features.polymeterTendency * 0.35)) return 16;
  const weights = [
    2, // 16 stays likely even when polymeter is on
    features.polymeterTendency,
    features.polymeterTendency * 0.9,
    features.polymeterTendency * 0.7,
    features.polymeterTendency * 0.5,
  ];
  return STEP_COUNT_CHOICES[rng.weighted(weights)] ?? 16;
}

/**
 * How much each step wants to sound.
 *
 * Downbeats and beat divisions score high; syncopation moves weight onto the
 * offbeats; regularity sharpens the contrast between strong and weak positions,
 * while a low value flattens it into something that floats.
 */
export function metricWeights(
  stepsPerBar: number,
  role: RhythmRole,
  features: GenreFeatures,
): number[] {
  const weights: number[] = [];
  const stepsPerBeat = stepsPerBar / 4;

  for (let step = 0; step < stepsPerBar; step += 1) {
    const positionInBeat = step % stepsPerBeat;
    const beat = Math.floor(step / stepsPerBeat);

    let weight: number;
    if (positionInBeat === 0) {
      // Beat 1 is strongest, beat 3 next: the usual metric hierarchy.
      weight = beat === 0 ? 1 : beat === 2 ? 0.8 : 0.65;
    } else if (Math.abs(positionInBeat - stepsPerBeat / 2) < 0.001) {
      weight = 0.45; // the "and"
    } else {
      weight = 0.2; // finer subdivisions
    }

    // Syncopation lifts the weak positions and shaves the strong ones.
    const offbeatness = positionInBeat === 0 ? 0 : 1;
    weight += offbeatness * features.syncopation * 0.55;
    weight -= (1 - offbeatness) * features.syncopation * 0.25;

    // Finer subdivisions become viable as subdivisionBias rises.
    if (positionInBeat !== 0) {
      weight += features.subdivisionBias * 0.25;
    }

    // Regularity sharpens the profile; low regularity flattens it toward equal
    // probability, which is what makes ambient placement feel unmoored.
    const flat = 0.45;
    weight = flat + (weight - flat) * (0.35 + features.rhythmicRegularity * 1.25);

    weights.push(Math.max(0.02, weight));
  }

  // The kick carries the pulse, so it leans harder on beat positions.
  if (role === "kick") {
    for (let step = 0; step < stepsPerBar; step += 1) {
      const onBeat = step % stepsPerBeat === 0;
      weights[step] = Math.max(
        0.02,
        (weights[step] ?? 0.2) *
          (onBeat ? 1 + features.pulseStrength * 2.2 : 1 - features.pulseStrength * 0.55),
      );
    }
  }

  return weights;
}

/**
 * Target event count for a role.
 *
 * Density is the only term that scales this. Genre biases the baseline (ambient
 * thins out, techno fills in) and silence takes events away, but Energy is
 * absent by construction.
 */
export function targetEventCount(
  role: RhythmRole,
  stepsPerBar: number,
  ctx: RhythmContext,
): number {
  const { features, density } = ctx;

  // Per-role share of the bar, as a fraction of available steps.
  const roleBase: Record<RhythmRole, { min: number; max: number }> = {
    kick: { min: 0.04, max: 0.3 },
    pulse: { min: 0.06, max: 0.62 },
    percussion: { min: 0.0, max: 0.34 },
    accent: { min: 0.0, max: 0.12 },
  };

  const band = roleBase[role];
  const genreScale = 0.45 + features.eventDensityBias * 0.9;
  const silenceScale =
    1 - clamp(ctx.silenceProbability * 0.6 + features.silenceTendency * 0.45, 0, 0.85);

  const fraction = (band.min + (band.max - band.min) * density) * genreScale * silenceScale;
  const raw = fraction * stepsPerBar;

  // Fills reshape the last beat rather than adding notes, so they must not
  // change the target count — otherwise Energy (which gates fills) would leak
  // into event count and break the independence guarantee.
  const count = Math.round(raw);
  return clamp(count, 0, MAX_EVENTS_PER_ROLE);
}

function hashPattern(pattern: StepPattern): string {
  return `${pattern.stepsPerBar}:${pattern.steps.join(",")}|${pattern.accents.join(",")}`;
}

export function patternHash(pattern: StepPattern | null): string {
  return pattern === null ? "-" : hashPattern(pattern);
}

/** Builds a fresh pattern from the weight profile. */
export function generatePattern(
  role: RhythmRole,
  stepsPerBar: number,
  ctx: RhythmContext,
): StepPattern {
  const weights = metricWeights(stepsPerBar, role, ctx.features);
  const count = targetEventCount(role, stepsPerBar, ctx);
  const steps = selectWeightedDistinct(weights, count, ctx.rng);

  return {
    stepsPerBar,
    steps,
    accents: chooseAccents(steps, stepsPerBar, ctx),
    ghosts: chooseGhosts(steps, ctx),
  };
}

/**
 * Accent placement.
 *
 * Accents are a HOW, not a HOW MANY: this only marks existing steps, so it is
 * allowed to depend on Energy (applied later in PerformanceEngine) without
 * affecting event count.
 */
function chooseAccents(steps: number[], stepsPerBar: number, ctx: RhythmContext): number[] {
  if (steps.length === 0) return [];
  const stepsPerBeat = stepsPerBar / 4;
  const accents: number[] = [];

  for (const step of steps) {
    const onBeat = step % stepsPerBeat === 0;
    // Regular styles accent the beat; syncopated ones displace the accent.
    const probability = onBeat
      ? 0.25 + ctx.features.rhythmicRegularity * 0.5
      : 0.1 + ctx.features.syncopation * 0.55;
    if (ctx.rng.chance(probability)) accents.push(step);
  }

  // A bar with no accent at all reads as flat; keep at least one anchor.
  const anchor = steps[0];
  if (accents.length === 0 && anchor !== undefined) {
    accents.push(anchor);
  }
  return accents;
}

/** Ghost notes are existing steps played quietly, so again: no count change. */
function chooseGhosts(steps: number[], ctx: RhythmContext): number[] {
  const ghosts: number[] = [];
  for (const step of steps) {
    if (ctx.rng.chance(ctx.features.ghostNoteTendency * 0.45)) ghosts.push(step);
  }
  return ghosts;
}

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

type MutationKind =
  | "rotate"
  | "add"
  | "remove"
  | "accentShift"
  | "ghostAdd"
  | "subdivide"
  | "merge"
  | "velocityCurve";

/**
 * Applies one mutation. The pattern evolves rather than being re-rolled, which
 * is what makes a techno-leaning mix feel like it is developing a single idea.
 */
export function mutatePattern(
  pattern: StepPattern,
  role: RhythmRole,
  ctx: RhythmContext,
): { pattern: StepPattern; kind: MutationKind } {
  const { features, rng } = ctx;
  const kinds: MutationKind[] = [
    "rotate",
    "add",
    "remove",
    "accentShift",
    "ghostAdd",
    "subdivide",
    "merge",
    "velocityCurve",
  ];
  const weights = [
    0.6 + features.polymeterTendency * 0.8,
    0.8,
    0.8,
    0.7 + features.syncopation * 0.8,
    0.3 + features.ghostNoteTendency * 1.2,
    0.4 + features.subdivisionBias * 0.9,
    0.4 + features.silenceTendency * 0.6,
    0.5,
  ];
  const kind = kinds[rng.weighted(weights)] ?? "rotate";

  const steps = [...pattern.steps];
  let accents = [...pattern.accents];
  let ghosts = [...pattern.ghosts];
  const n = pattern.stepsPerBar;

  switch (kind) {
    case "rotate": {
      const by = rng.int(1, Math.max(1, Math.floor(n / 4)));
      const rotated = steps.map((s) => (s + by) % n).sort((a, b) => a - b);
      accents = accents.map((s) => (s + by) % n);
      ghosts = ghosts.map((s) => (s + by) % n);
      return {
        pattern: { stepsPerBar: n, steps: rotated, accents, ghosts },
        kind,
      };
    }
    case "add": {
      const weights2 = metricWeights(n, role, features).map((w, i) => (steps.includes(i) ? 0 : w));
      const [added] = selectWeightedDistinct(weights2, 1, rng);
      if (added !== undefined && steps.length < MAX_EVENTS_PER_ROLE) steps.push(added);
      break;
    }
    case "remove": {
      if (steps.length > 1) {
        const index = rng.int(0, steps.length - 1);
        const removed = steps.splice(index, 1)[0];
        accents = accents.filter((s) => s !== removed);
        ghosts = ghosts.filter((s) => s !== removed);
      }
      break;
    }
    case "accentShift": {
      if (steps.length > 0) {
        const from = accents.length > 0 ? rng.int(0, accents.length - 1) : -1;
        if (from >= 0) accents.splice(from, 1);
        const candidate = steps[rng.int(0, steps.length - 1)];
        if (candidate !== undefined && !accents.includes(candidate)) accents.push(candidate);
      }
      break;
    }
    case "ghostAdd": {
      const candidates = steps.filter((s) => !ghosts.includes(s) && !accents.includes(s));
      const candidate = candidates[rng.int(0, candidates.length - 1)];
      if (candidate !== undefined) {
        ghosts.push(candidate);
      }
      break;
    }
    case "subdivide": {
      // Fill a gap next to an existing step: the classic "double it up" move.
      if (steps.length > 0 && steps.length < MAX_EVENTS_PER_ROLE) {
        const anchor = steps[rng.int(0, steps.length - 1)];
        const neighbour = anchor === undefined ? -1 : (anchor + 1) % n;
        if (neighbour >= 0 && !steps.includes(neighbour)) {
          steps.push(neighbour);
          ghosts.push(neighbour);
        }
      }
      break;
    }
    case "merge": {
      // Remove a step that sits next to another, thinning a cluster.
      const clustered = steps.filter((s) => steps.includes((s + 1) % n));
      if (clustered.length > 0 && steps.length > 1) {
        const victim = clustered[rng.int(0, clustered.length - 1)];
        if (victim !== undefined) {
          const at = steps.indexOf(victim);
          if (at >= 0) steps.splice(at, 1);
          accents = accents.filter((s) => s !== victim);
          ghosts = ghosts.filter((s) => s !== victim);
        }
      }
      break;
    }
    case "velocityCurve": {
      // Purely expressive: re-roll which steps are accented, keeping the count.
      accents = chooseAccents(steps, n, ctx);
      break;
    }
  }

  const sorted = [...new Set(steps)].sort((a, b) => a - b);
  return {
    pattern: {
      stepsPerBar: n,
      steps: sorted,
      accents: accents.filter((s) => sorted.includes(s)),
      ghosts: ghosts.filter((s) => sorted.includes(s)),
    },
    kind,
  };
}

/**
 * A fill reshapes the final beat instead of adding to the bar, so the bar's
 * event count stays where Density put it.
 */
export function applyFill(pattern: StepPattern, role: RhythmRole, ctx: RhythmContext): StepPattern {
  const n = pattern.stepsPerBar;
  const lastBeatStart = Math.floor((n * 3) / 4);
  const kept = pattern.steps.filter((s) => s < lastBeatStart);
  const removedCount = pattern.steps.length - kept.length;
  if (removedCount === 0) return pattern;

  const weights = metricWeights(n, role, ctx.features).map((w, i) =>
    i >= lastBeatStart ? w + ctx.features.subdivisionBias * 0.6 : 0,
  );
  const replacement = selectWeightedDistinct(weights, removedCount, ctx.rng);
  const steps = [...kept, ...replacement].sort((a, b) => a - b);

  return {
    stepsPerBar: n,
    steps,
    accents: pattern.accents.filter((s) => steps.includes(s)).concat(replacement.slice(0, 1)),
    ghosts: pattern.ghosts.filter((s) => steps.includes(s)),
  };
}

// ---------------------------------------------------------------------------
// Per-bar decision
// ---------------------------------------------------------------------------

/**
 * Produces this bar's pattern for one role: repeat, mutate or regenerate.
 *
 * The recent-hash check exists because a high-repetition mix can otherwise
 * settle into a two-bar ABAB loop, which the spec explicitly forbids.
 */
export function stepRolePattern(
  role: RhythmRole,
  roleState: RoleRhythmState,
  ctx: RhythmContext,
): StepPattern {
  const { features, rng } = ctx;
  const previous = roleState.pattern;

  let pattern: StepPattern;

  if (previous === null) {
    pattern = generatePattern(role, chooseStepsPerBar(features, rng), ctx);
  } else {
    const targetCount = targetEventCount(role, previous.stepsPerBar, ctx);
    const countDrift = Math.abs(previous.steps.length - targetCount);

    // A large density change has to be honoured even in a repetitive style.
    const mustRebuild = countDrift > Math.max(1, Math.round(previous.steps.length * 0.4));
    const repeat = !mustRebuild && rng.chance(features.patternRepetition * 0.8);

    if (repeat) {
      pattern = previous;
    } else if (!mustRebuild && rng.chance(features.patternMutation)) {
      pattern = mutatePattern(previous, role, ctx).pattern;
    } else {
      pattern = generatePattern(role, chooseStepsPerBar(features, rng), ctx);
    }
  }

  if (ctx.isFill) {
    pattern = applyFill(pattern, role, ctx);
  }

  // Break an A-B-A-B oscillation by forcing a mutation.
  const hash = hashPattern(pattern);
  const recent = roleState.recentHashes;
  if (
    recent.length >= 3 &&
    recent[recent.length - 2] === hash &&
    recent[recent.length - 1] !== hash
  ) {
    pattern = mutatePattern(pattern, role, ctx).pattern;
  }

  roleState.recentHashes.push(hashPattern(pattern));
  if (roleState.recentHashes.length > 8) roleState.recentHashes.shift();
  roleState.barsSinceMutation = pattern === previous ? roleState.barsSinceMutation + 1 : 0;
  roleState.pattern = pattern;

  return pattern;
}
